import classNames from 'classnames';
import React, { useState } from 'react';
import HubspotFormItem from '@/components/modules/hubspot-form/hubspot-form-item/hubspot-form-item';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import Button from '@/components/molecules/button/button';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import styles from './cta-link-list.module.scss';

type CtaLink =
  | {
      $type: 'ButtonLinkCtaDto';
      link: Content.Components['ButtonLinkCtaDto']['link'];
      settings?: Content.Components['ButtonLinkCtaDto']['settings'];
    }
  | {
      $type: 'HubspotFormCtaDto';
      hubspotForm: Content.Components['HubspotFormCtaDto']['hubspotForm'];
      text: Content.Components['HubspotFormCtaDto']['text'];
    }
  | {
      $type: 'LinkCtaDto';
      link: Content.Components['LinkCtaDto']['link'];
    };

export interface CtaLinkListProps {
  /** The class name to apply */
  className?: string;
  /** Indicates whether this is used in a highlight-section */
  isHighlightSection?: boolean;
  /** The class name to apply to each item */
  itemClassName?: string;
  /** An array of items to show */
  items: CtaLink[];
}

const CtaLinkList = ({
  className,
  itemClassName,
  isHighlightSection = false,
  items = [],
}: CtaLinkListProps) => {
  const [activeHubspotFormIndex, setActiveHubspotFormIndex] = useState<
    number | null
  >(null);

  if (!items.length) return null;

  const renderContent = (item: CtaLink, itemIndex: number) => {
    const itemClassNames = classNames(styles.item, itemClassName);

    if (item.$type === 'LinkCtaDto' && item.link) {
      return (
        <AnchorLink
          key={itemIndex}
          className={itemClassNames}
          icon="ArrowRight"
          isHighlightSection={isHighlightSection}
          {...item.link}
        />
      );
    }

    if (item.$type === 'ButtonLinkCtaDto' && item.link) {
      return (
        <Button
          key={itemIndex}
          className={itemClassNames}
          isHighlightSection={isHighlightSection}
          variant={item.settings?.variant}
          {...item.link}
        />
      );
    }

    if (item.$type === 'HubspotFormCtaDto') {
      return (
        <React.Fragment key={itemIndex}>
          <Button
            className={itemClassNames}
            isHighlightSection={isHighlightSection}
            onClick={() => setActiveHubspotFormIndex(itemIndex)}
            text={item.text}
          />

          <Lightbox
            isOpen={activeHubspotFormIndex === itemIndex}
            onClose={() => setActiveHubspotFormIndex(null)}
          >
            <HubspotFormItem form={item.hubspotForm} />
          </Lightbox>
        </React.Fragment>
      );
    }
  };

  return (
    <div className={classNames(styles.component, className)}>
      {items.map((item, itemIndex) => renderContent(item, itemIndex))}
    </div>
  );
};

CtaLinkList.displayName = 'CtaLinkList';

export default CtaLinkList;
